import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { APP_INITIALIZER, ApplicationConfig, importProvidersFrom, provideZonelessChangeDetection } from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, withComponentInputBinding, withDebugTracing } from '@angular/router';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { LOCAL_STORAGE_ENGINE, SESSION_STORAGE_ENGINE, withNgxsStoragePlugin } from '@ngxs/storage-plugin';
import { loadRuntimeConfig } from '@tsin-core/config/config.loader';
import { ConfigService } from '@tsin-core/services/common/config.service';
import { LocaleService } from '@tsin-core/services/common/locale.service';

import { BrowserAnimationsModule, provideAnimations } from '@angular/platform-browser/animations';
import { NgxsReduxDevtoolsPluginModule, withNgxsReduxDevtoolsPlugin } from '@ngxs/devtools-plugin';
import { withNgxsFormPlugin } from '@ngxs/form-plugin';
import { NgxsLoggerPluginModule, withNgxsLoggerPlugin } from '@ngxs/logger-plugin';
import { withNgxsRouterPlugin } from '@ngxs/router-plugin';
import { provideStore } from '@ngxs/store';
import { withNgxsWebSocketPlugin } from '@ngxs/websocket-plugin';
import { jwtInterceptor } from '@tsin-core/interceptors/jwt.interceptor';
import { loggingInterceptor } from '@tsin-core/interceptors/logging.interceptor';
import { AdministrationState } from '@tsin-core/states/administration/administration.state';
import { ApplicationsState } from '@tsin-core/states/applications/applications.state';
import { AuthState } from '@tsin-core/states/auth.state';
import { CommunicationsState } from '@tsin-core/states/communications/communications.state';
import { EmailTrackingState } from '@tsin-core/states/email-tracking/email-tracking.state';
import { InterviewerState } from '@tsin-core/states/interviewer/interviewer.state';
import { ModeratorState } from '@tsin-core/states/moderator/moderator.state';
import { PortalDrawState } from '@tsin-core/states/portal-draw/portal-draw.state';
import { PreScreeningState } from '@tsin-core/states/pre-screening/pre-screening.state';
import { ProExamState } from '@tsin-core/states/pro-exam/pro-exam.state';
import { QuestionState } from '@tsin-core/states/question/question.state';
import { ScreenerQueueState } from '@tsin-core/states/screener-queue/screener-queue.state';
import { SettingsState } from '@tsin-core/states/settings/settings.state';
import { StationState } from '@tsin-core/states/station/station.state';
import { TrackState } from '@tsin-core/states/track/track.state';
import { VerifyDocsState } from '@tsin-core/states/verify-docs/verify-docs.state';
import { environment } from '@tsin-env/environment';
import { MyPreset } from '@tsin-shared/utils/theme';
import { providePrimeNG } from 'primeng/config';
import { routes } from './app.routes';

export const appConfig: ApplicationConfig = {
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: loadRuntimeConfig,
      deps: [ConfigService],
      multi: true,
    },
    provideZonelessChangeDetection(),
    provideRouter(routes, withComponentInputBinding(), withDebugTracing()),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: MyPreset,
        options: {
          darkModeSelector: '.my-app-dark',
        },
      },
    }),
    // ── i18n ─────────────────────────────────────────────────────────────────
    provideTranslateService({ fallbackLang: 'en' }),
    provideTranslateHttpLoader({ prefix: '/assets/i18n/', suffix: '.json' }),
    {
      provide: APP_INITIALIZER,
      useFactory: (localeService: LocaleService) => () => localeService.initFromUrl(),
      deps: [LocaleService],
      multi: true,
    },
    // ── Animations & plugins ─────────────────────────────────────────────────
    importProvidersFrom(
      BrowserAnimationsModule,
      NgxsLoggerPluginModule.forRoot({
        disabled: environment.production,
      }),
      NgxsReduxDevtoolsPluginModule.forRoot({
        disabled: environment.production,
      }),
    ),
    provideAnimations(),
    provideHttpClient(withInterceptors([jwtInterceptor, loggingInterceptor])),
    provideStore(
      [AuthState, ProExamState, AdministrationState, PreScreeningState, TrackState, StationState, InterviewerState, ModeratorState, ApplicationsState, VerifyDocsState, PortalDrawState, QuestionState, SettingsState, ScreenerQueueState, CommunicationsState, EmailTrackingState],
      withNgxsStoragePlugin({
        keys: [
          // {
          //   key: 'moodleState.users',
          //   engine: LOCAL_STORAGE_ENGINE,
          // },
          {
            key: 'proExamState.selectedExam',
            engine: LOCAL_STORAGE_ENGINE,
          },
          {
            key: 'questionState.questions',
            engine: LOCAL_STORAGE_ENGINE,
          },
          {
            key: 'stationState.stations',
            engine: LOCAL_STORAGE_ENGINE,
          },
          {
            key: 'preScreeningState.preScreening',
            engine: LOCAL_STORAGE_ENGINE,
          },
          {
            key: 'proExamState.exams',
            engine: LOCAL_STORAGE_ENGINE,
          },
          {
            key: 'trackState.tracks',
            engine: LOCAL_STORAGE_ENGINE,
          },
          {
            key: 'administrationState.administrations',
            engine: LOCAL_STORAGE_ENGINE,
          },
          {
            key: 'applicationState.selectedApplication',
            engine: LOCAL_STORAGE_ENGINE,
          },
          {
            key: 'applicationState.reviewApplication',
            engine: LOCAL_STORAGE_ENGINE,
          },
          {
            key: 'applicationState.applications',
            engine: LOCAL_STORAGE_ENGINE,
          },
          {
            key: 'applicationState.reviewPhase2',
            engine: LOCAL_STORAGE_ENGINE,
          },
          {
            key: 'portalDrawState.selectedPortalDraw',
            engine: LOCAL_STORAGE_ENGINE,
          },
          {
            key: 'proExamState.selectedExam',
            engine: LOCAL_STORAGE_ENGINE,
          },
          {
            key: 'authState.auth',
            engine: SESSION_STORAGE_ENGINE,
          },
          // Cohort/draw scope of the screener queue — kept for the tab's
          // session so the worklist survives a refresh, not beyond it.
          // Portée (cohorte/tirage) de la file d'examen, conservée pour la session.
          {
            key: 'screenerQueueState.filter',
            engine: SESSION_STORAGE_ENGINE,
          },
        ],
      }),
      withNgxsReduxDevtoolsPlugin(),
      withNgxsFormPlugin(),
      withNgxsLoggerPlugin(),
      withNgxsRouterPlugin(),
      withNgxsWebSocketPlugin(),
    ),
  ],
};
